import "./EmailLoginForm.scss";

import { AxiosError } from "axios";
import { FC, useEffect, useState } from "react";
import { useBoolean } from "usehooks-ts";

import { getPasswordErrorMessages } from "../../helpers/password-errors";

import {
  serverGetCourseMetadataFromInviteKey,
  serverRegisterUserWithInviteKey,
} from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import { LOGO_FORM_SIZE } from "components/constants";
import { FormattedValidationError } from "components/server-types";
import { AccountForm, AccountFormValue } from "components/sso/AccountForm";
import { LoginView } from "components/sso/LoginView";
import { Trans, t } from "i18n/i18n";
import { PasswordErrorKey } from "i18n/i18next";
import { LegacyButton } from "mds/components/LegacyButton";
import { ForumLogoIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectCurrentCourseCodeTitle,
  selectCurrentCourseId,
  selectCurrentCourseTitle,
  selectCurrentOrgName,
  selectCurrentTermTitle,
  selectCurrentUserId,
} from "store/selectors";
import { toastErrorMessage, toastLocalizedOperationCatcher } from "utils/alerts";
import { trackEvent } from "utils/amplitude";
import { useQueryParamsManager } from "utils/routing";
import { courseHomeUrl } from "utils/urls";

interface SignupViewProps {
  checkActiveUser: (justLoggedIn?: boolean) => void;
  userInviteError?: string;
  setUserInviteError?: (error: string | null) => void;
}

/**
 * A view with a registration form, for users who are not logged in and have an invite key.
 *
 * We don't currently support registration outside of following invite links, so
 * users arriving at this view will be expected to have an `invite_key` query parameter set,
 * and not be logged in already.
 * They'll be show a registration page to create an account and join the course. After creation,
 * the user will be redirected to the course page.
 *
 * Logged-in user with an `invite_key` should never be shown this view, as the index route
 * should redeem the key to ensure the user is part of the desired course before
 * forwarding them to the course route.
 */
export const SignupView: FC<SignupViewProps> = ({
  checkActiveUser,
  userInviteError,
  setUserInviteError,
}) => {
  const { getQueryParam } = useQueryParamsManager();
  const inviteKey = getQueryParam("invite_key");
  const userId = useAppSelector(selectCurrentUserId);
  const currentCourseId = useAppSelector(selectCurrentCourseId);

  // These values used only for event tracking
  // TODO:  These values tend to be null when we need them
  // so either remove from event or add logic to wait for them
  // to have values
  const currentOrgName = useAppSelector(selectCurrentOrgName) || "";
  const courseCodeTitle = useAppSelector(selectCurrentCourseCodeTitle) || "";
  const currentCourseTitle = useAppSelector(selectCurrentCourseTitle) || "";
  const currentTermTitle = useAppSelector(selectCurrentTermTitle) || "";

  const [isLoading, setIsLoading] = useState<boolean>(!userInviteError);
  const [inviteError, setInviteError] = useState<string | null>(userInviteError);
  const [courseTitle, setCourseTitle] = useState<string | null>(null);
  const {
    value: isShowingLoginView,
    setTrue: showLoginView,
    setFalse: hideLoginView,
  } = useBoolean(false);

  useEffect(() => {
    const fetchCourseTitle = async () => {
      try {
        const course = await serverGetCourseMetadataFromInviteKey(inviteKey);
        setCourseTitle(course.title);
      } catch (error) {
        const errorKey =
          error instanceof AxiosError && error.response?.status === 403
            ? "invalid_invite_key"
            : "unknown_error";

        setInviteError(t(`user_options_menu.redeem.${errorKey}`));
      } finally {
        setIsLoading(false);
      }
    };

    if (inviteKey && !inviteError) {
      fetchCourseTitle();
    }
  }, [setCourseTitle, inviteKey, inviteError]);

  const handleSubmit = async (formData: AccountFormValue) => {
    try {
      await serverRegisterUserWithInviteKey(inviteKey, {
        ...formData,
        // TODO: Rename password_confirm to confirm_password on the CourseInvite model.
        password_confirm: formData.confirm_password,
        eula_accepted: formData.eula,
      });

      window.location.href = "/";

      trackEvent("Account - Sign up successful", {
        eventCategory: "General",
        institutionName: currentOrgName,
        courseCode: courseCodeTitle,
        courseTitle: currentCourseTitle,
        courseTerm: currentTermTitle,
      });
    } catch (error) {
      trackEvent("Account - Sign up failed", {
        eventCategory: "General",
        institutionName: currentOrgName,
        courseCode: courseCodeTitle,
        courseTitle: currentCourseTitle,
        courseTerm: currentTermTitle,
      });

      if (error instanceof AxiosError && error.response?.status === 400 && error.response?.data) {
        const data = error.response?.data as
          | FormattedValidationError<PasswordErrorKey>
          | { email: string[] };

        let message = "";
        if ("email" in data) {
          message = (data as { email: string[] }).email.join(" "); // TODO: Localize email errors
        } else {
          for (const [, { details, t: translationKeys }] of Object.entries(data)) {
            message += getPasswordErrorMessages(translationKeys, details);
          }
        }

        if (!message) {
          toastLocalizedOperationCatcher("signup_action_failure")(error);
        } else {
          toastErrorMessage(message);
        }
      } else {
        toastLocalizedOperationCatcher("signup_action_failure")(error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="m-2 w-full max-w-96 p-4">
        <LoadingScreen />
      </div>
    );
  }

  if (inviteError) {
    // If we do sign in here, we don't want to include the bad invitation but rather
    // start fresh from the course home page.
    const buttonKey = userId ? "main_header.back_to_home" : "user_info.sign_in";

    return (
      <div className="m-2 w-full max-w-96 p-4">
        <h2 className="my-2">{inviteError}</h2>

        <div className="mt-8">
          {!userId && t("user_options_menu.redeem.already_have_account")}

          <LegacyButton
            kind="secondary"
            to={courseHomeUrl(currentCourseId)}
            // This call here is actually to force the parent component to reload.  Without it the
            // url might switch but the parent wouldn't refresh to point to the new location.
            onClick={() => setUserInviteError?.(null)}
          >
            {t(buttonKey)}
          </LegacyButton>
        </div>
      </div>
    );
  }

  if (isShowingLoginView) {
    return (
      <div className="m-2 w-full max-w-96">
        <LoginView onLoggedIn={() => checkActiveUser(true)}>
          <div className="w-full">
            <LegacyButton className="mt-4" kind="secondary" size="s" onClick={hideLoginView}>
              {t("user_options_menu.redeem.back_to_sign_up")}
            </LegacyButton>
          </div>
        </LoginView>
      </div>
    );
  }

  return (
    <div className="m-2 w-full max-w-96 p-4">
      <div className="mb-3 flex w-full items-center justify-between border-b border-black-tint-90 bg-white pb-1 text-black-tint-20">
        <span className="mb-2 flex cursor-pointer items-center">
          <ForumLogoIcon width={LOGO_FORM_SIZE} />
        </span>
      </div>

      <h2 className="mb-2">{t("user_options_menu.redeem.signup_title")}</h2>

      <div className="body-m mb-4 pt-1">
        <Trans
          components={{ bold: <b /> }}
          i18nKey="user_options_menu.redeem.signup_subtitle"
          t={t}
          values={{ courseTitle }}
        />
      </div>

      <AccountForm isLoading={isLoading} onShowLogin={showLoginView} onSubmit={handleSubmit} />
    </div>
  );
};
